import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { errorToast } from '../components/Toast';
import { AuthData } from '../context/auth';

const isLocationLocal = () => {
	const host = window?.location?.host;
	return host.startsWith('localhost');
};

export const CORS_MODE = isLocationLocal() ? 'no-cors' : undefined;
export const AUTHORIZATION_INVALID_STATUS_CODES: number[] = [401, 417];
export const NOT_FOUND_ERRORS: number[] = [404];
export const BAD_USER_PERMISSION_ERRORS: number[] = [403];
export const VALIDATION_ERRORS: number[] = [422];
export const BAD_ERRORS: number[] = [500, 400, 412, 409]; // These will print response.message on the error modal.
export const UNSAVED_CHANGES_ERRORS: number[] = [406];

/** returns true if there is authorization error */
export const handleUnauthorizedResponse = async (
	error: AxiosError,
	authData: AuthData,
) => {
	console.error(error);
	if (
		error.response &&
		AUTHORIZATION_INVALID_STATUS_CODES.includes(error.response.status)
	) {
		if (!authData.ssoEnabled) {
			errorToast('Session expired, please sign in again.');
			await authData.logout();
		} else {
			await authData.setIsLoggedOut(true);
			return true;
		}
	}

	if (
		error.response &&
		BAD_USER_PERMISSION_ERRORS.includes(error.response.status)
	) {
		errorToast(error.response.data?.message || 'Something went wrong.');

		return true;
	}

	return false;
};

export async function get<T>(
	url: string,
	jwt?: string,
	config?: AxiosRequestConfig<any>,
): Promise<AxiosResponse<T>> {
	const headers = {
		Authorization: jwt,
		'Socotra-Ontologyuri-Expanded': 'true',
	};

	return axios.get(url, {
		headers,
		...(config || {}),
	});
}

export async function getSso<T>(
	url: string,
	jwt?: string,
): Promise<AxiosResponse<T>> {
	const headers = {
		'Socotra-Ontologyuri-Expanded': 'true',
	};

	return axios.get(url, {
		headers,
	});
}

export async function patch<T>(
	url: string,
	jwt?: string,
	data?: any,
): Promise<AxiosResponse<T>> {
	return axios.patch(url, data, buildOptions(jwt));
}

export async function post<T>(
	url: string,
	jwt?: string,
	data?: any,
	config?: AxiosRequestConfig<any>,
	customHeaders?: any,
): Promise<AxiosResponse<T>> {
	return axios.post(url, data, {
		...buildOptions(jwt, customHeaders),
		...config,
	});
}

export async function put<T>(
	url: string,
	jwt?: string,
	data?: any,
): Promise<AxiosResponse<T>> {
	return axios.put(url, data, buildOptions(jwt));
}

export async function deleteRequest<T>(
	url: string,
	jwt?: string,
	data?: any,
): Promise<AxiosResponse<T>> {
	return axios.delete(url, buildOptions(jwt));
}

function buildOptions(jwt?: string, customHeaders?: any) {
	const headers = {
		Authorization: jwt,
		'Content-Type': 'application/json; charset=UTF-8',
		'Socotra-Ontologyuri-Expanded': 'true',
		...customHeaders,
	};
	return {
		headers,
	};
}
